import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { widthPercentageToDP as wp } from 'react-native-responsive-screen';
import DropDownPicker from 'react-native-dropdown-picker';
import { Feather } from '@expo/vector-icons';

import ToggleSwitch from '../ToggleSwitch';
import DateInput from '../DateInput';
import { useResponsiveStyles } from '../../hooks';
import AppStyles from '../../AppStyles';
import { t as translate, useTranslation } from '../../i18n';

// Wrapper component that adds an Apply button below an open dropdown
const DropdownWithApplyButton = ({ children, isOpen, onApply }) => (
  <View>
    {children}
    {isOpen && (
      <Pressable
        onPress={onApply}
        style={({ pressed, hovered, focused }) => [
          styles.applyButton,
          { cursor: 'pointer' },
          (hovered || focused) && styles.applyButtonHovered,
          pressed && { opacity: 0.8 },
        ]}
      >
        <Feather name="check" size={16} color="#000" style={{ marginRight: 6 }} />
        <Text style={styles.applyButtonText}>{translate('reports:filterPanel.applyFilter')}</Text>
      </Pressable>
    )}
  </View>
);

const ReportFilterPanel = ({

  // Toggle filter
  includePreviousReports,
  onToggleIncludePreviousReports,
  // HRO filter
  HROFilterOpen,
  setHROFilterOpen,
  HROFilterValue,
  setHROFilterValue,
  HROFilter,
  setHROFilter,
  // KPI filter
  KPIFilterOpen,
  setKPIFilterOpen,
  KPIFilterValue,
  setKPIFilterValue,
  KPIFilter,
  setKPIFilter,
  // Organization filter
  OrgFilter,
  OrgFilterOpen,
  setOrgFilterOpen,
  OrgFilterValue,
  setOrgFilterValue,
  setOrgFilter,
  loadingOrg,
  // Date range filter
  dateRangeStart,
  setDateRangeStart,
  dateRangeEnd,
  setDateRangeEnd,
}) => {
  const { t } = useTranslation('reports');
  const { screenWidth } = useResponsiveStyles();
  const isWeb = screenWidth >= 800;

  return (
    <View style={styles.container}>
      {/* Row 1: Toggle and Organization */}
      <View style={[styles.row, isWeb && styles.rowWeb, { zIndex: OrgFilterOpen ? 2 : 1 }]}>
        <View style={[styles.filterItem, isWeb ? styles.filterItemWebLeft : styles.filterItemMobile]}>
          <ToggleSwitch
            value={includePreviousReports}
            onValueChange={onToggleIncludePreviousReports}
          />
          <Text style={styles.toggleText}>
            {t('filterPanel.noResponseOnly')}
          </Text>
        </View>

        {OrgFilter?.length > 1 && (
          <View style={[isWeb ? styles.dropdownWeb : styles.dropdownMobile, { zIndex: 10 }]}>
            <DropdownWithApplyButton isOpen={OrgFilterOpen} onApply={() => setOrgFilterOpen(false)}>
              <DropDownPicker
                loading={loadingOrg}
                zIndex={9}
                multiple={true}
                min={0}
                open={OrgFilterOpen}
                value={OrgFilterValue}
                items={OrgFilter}
                setOpen={setOrgFilterOpen}
                setValue={setOrgFilterValue}
                setItems={setOrgFilter}
                dropDownDirection="TOP"
                placeholder={t('filterPanel.filterByOrganization')}
                maxHeight={140}
                listMode="SCROLLVIEW"
              />
            </DropdownWithApplyButton>
          </View>
        )}
      </View>

      {/* Row 2: HRO and KPI filters */}
      <View style={[styles.row, isWeb && styles.rowWeb, { zIndex: (HROFilterOpen || KPIFilterOpen) ? 2 : 1 }]}>
        <View style={isWeb ? styles.dropdownWeb : styles.dropdownMobile}>
          <DropdownWithApplyButton isOpen={HROFilterOpen} onApply={() => setHROFilterOpen(false)}>
            <DropDownPicker
              zIndex={9}
              multiple={true}
              min={0}
              open={HROFilterOpen}
              value={HROFilterValue}
              items={HROFilter}
              setOpen={setHROFilterOpen}
              setValue={setHROFilterValue}
              setItems={setHROFilter}
              dropDownDirection="TOP"
              placeholder={t('filterPanel.filterByHro')}
              maxHeight={200}
              listMode="SCROLLVIEW"
            />
          </DropdownWithApplyButton>
        </View>

        <View style={isWeb ? styles.dropdownWeb : styles.dropdownMobile}>
          <DropdownWithApplyButton isOpen={KPIFilterOpen} onApply={() => setKPIFilterOpen(false)}>
            <DropDownPicker
              zIndex={9}
              multiple={true}
              min={0}
              open={KPIFilterOpen}
              value={KPIFilterValue}
              items={KPIFilter}
              setOpen={setKPIFilterOpen}
              setValue={setKPIFilterValue}
              setItems={setKPIFilter}
              dropDownDirection="TOP"
              searchable={true}
              categorySelectable={false}
              placeholder={t('filterPanel.filterByHazard')}
              maxHeight={200}
              listMode="SCROLLVIEW"
              listParentLabelStyle={{ fontWeight: 'bold' }}
            />
          </DropdownWithApplyButton>
        </View>
      </View>

      {/* Row 3: Date range */}
      <View style={[styles.row, { marginBottom: 8 }]}>
        <View style={styles.dateRangeRow}>
          <Feather name="calendar" size={16} color={AppStyles.colorSet.textSecondary} />
          <Text style={styles.dateRangeLabel}>{t('filterPanel.submitted')}</Text>
          <DateInput
            value={dateRangeStart || ''}
            onCommit={setDateRangeStart}
            max={dateRangeEnd}
            accessibilityLabel={t('filterPanel.startDateLabel')}
          />
          <Text style={styles.dateRangeLabel}>{t('filterPanel.dateRangeTo')}</Text>
          <DateInput
            value={dateRangeEnd || ''}
            onCommit={setDateRangeEnd}
            min={dateRangeStart}
            accessibilityLabel={t('filterPanel.endDateLabel')}
          />
          {(dateRangeStart || dateRangeEnd) ? (
            <Pressable
              onPress={() => {
                setDateRangeStart('');
                setDateRangeEnd('');
              }}
              style={({ hovered, focused, pressed }) => [
                styles.dateClearButton,
                (hovered || focused) && { opacity: 0.8 },
                pressed && { opacity: 0.6 },
              ]}
              accessibilityLabel={t('filterPanel.clearDatesLabel')}
            >
              <Feather name="x-circle" size={14} color={AppStyles.colorSet.textSecondary} />
              <Text style={styles.dateClearText}>{t('filterPanel.clear')}</Text>
            </Pressable>
          ) : null}
        </View>
      </View>

    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    backgroundColor: 'rgba(200, 200, 200, 0.3)',
    paddingVertical: 10,
    paddingHorizontal: 2,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    marginHorizontal: wp(0.5),
  },
  row: {
    width: '100%',
    flex: 1,
    marginBottom: 20,
  },
  rowWeb: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  filterItem: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
  },
  filterItemWebLeft: {
    marginLeft: 5,
    width: '40%',
  },
  filterItemMobile: {
    marginLeft: 1,
    marginBottom: 5,
    width: '100%',
  },
  toggleText: {
    marginHorizontal: wp(2),
    marginVertical: 5,
  },
  dropdownWeb: {
    marginLeft: 5,
    width: '40%',
  },
  dropdownMobile: {
    marginLeft: 1,
    marginBottom: 5,
    width: '100%',
  },
  applyButton: {
    flexDirection: 'row',
    backgroundColor: AppStyles.colorSet.brandPrimary,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    marginTop: 4,
  },
  // Web hover/focus feedback — subtle lift on the filled Apply button
  applyButtonHovered: {
    opacity: 0.92,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 2,
  },
  applyButtonText: {
    color: '#000',
    fontWeight: '600',
    fontSize: 14,
  },
  dateRangeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginLeft: 5,
  },
  dateRangeLabel: {
    fontSize: 13,
    color: '#4B5563',
  },
  dateClearButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  dateClearText: {
    fontSize: 13,
    color: '#4B5563',
  },
});

export default ReportFilterPanel;
